import { useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { MessageDescriptor } from 'react-intl';
import { useMessagingStore, useChatRehydration } from '../hooks/index.ts';
import { useMessagingService } from '../hooks/useMessagingService.ts';
import { StageHeader, type AgentLogo } from './StageHeader.tsx';
import { StageLayout } from './StageLayout.tsx';
import { StageVoice } from './StageVoice.tsx';
import { useStageController } from './useStageController.ts';
import { useStageAppearance } from './useStageAppearance.ts';
import { useA2uiDemoHook } from './demo-fixture.ts';

export interface StageShellProps {
  logo?: AgentLogo;
  logoHref?: string;
  headerBrandName?: string;
  brandDescriptor?: MessageDescriptor;
  contactLabel?: MessageDescriptor;
  contactHref?: string;
  advisorLabel?: MessageDescriptor;
  welcomeEyebrow?: MessageDescriptor;
  starterPrompts?: readonly MessageDescriptor[];
  appearance?: 'light' | 'dark' | 'auto';
  placeholder?: MessageDescriptor;
  preChatNotice?: MessageDescriptor;
  postInputNote?: MessageDescriptor;
  errorMessage?: MessageDescriptor;
  emailAddress?: string;
}

/** Standalone entry with the normal agent arrival and parent preview integration. */
export const StageShell = observer(function StageShell({
  logo,
  logoHref,
  headerBrandName,
  brandDescriptor,
  contactLabel,
  contactHref,
  advisorLabel,
  welcomeEyebrow,
  starterPrompts,
  appearance = 'light',
  placeholder,
  preChatNotice,
  postInputNote,
  errorMessage,
  emailAddress,
}: StageShellProps) {
  useMessagingService();
  useStageAppearance(appearance);
  useA2uiDemoHook();
  const store = useMessagingStore();
  const [initError, setInitError] = useState(false);
  const onError = useCallback(() => setInitError(true), []);
  const connection = useChatRehydration({ onError });
  const stage = useStageController({
    connection: { ...connection, initError },
    canSubmit: connection.isReady && connection.connectionStatus === 'connected',
    submitMessage: store.sendMessage,
  });
  return (
    <div className="stage-frame">
      <StageLayout
        stage={stage}
        placeholder={placeholder}
        preChatNotice={preChatNotice}
        postInputNote={postInputNote}
        errorMessage={errorMessage}
        emailAddress={emailAddress}
        advisorLabel={advisorLabel}
        welcomeEyebrow={welcomeEyebrow}
        starterPrompts={starterPrompts}
        header={
          <StageHeader
            logo={logo}
            logoHref={logoHref}
            headerBrandName={headerBrandName}
            brandDescriptor={brandDescriptor}
            contactLabel={contactLabel}
            contactHref={contactHref}
            onRestart={stage.sendMessage}
            disabled={stage.runInFlight || !stage.ready}
          />
        }
      >
        <StageVoice screenSelection={stage.voiceScreenSelection} onRecord={stage.sendAudio} />
      </StageLayout>
    </div>
  );
});
